import { lazy } from 'react';
import { bookList } from 'src/constants/bookList';
import { ICON_NAMES } from 'src/components/library/icon/icon-constants';
import {
    ALL_AUTHENTICATED_ROLES,
    ADMIN_ROLES,
    ROLE_GROUPS,
    AGENCY_TYPES,
} from 'src/constants/roles.constants';
import { NAV_SECTIONS, NAV_AUDIENCE } from 'src/config/navigation.constants';
import { FEATURE_FLAG_KEYS } from 'src/config/featureFlags';

const liveOperations = lazy(() => import('src/routes/live'));
const alerts = lazy(() => import('src/routes/alerts'));
const transitInsights = lazy(() => import('src/routes/transit-insights'));
const systemHealth = lazy(() => import('src/routes/system-health'));
const team = lazy(() => import('src/routes/team/Team'));
const requests = lazy(() => import('src/routes/users-and-permissions/requests'));
const lytTeamManagement = lazy(() => import('src/routes/lyt-team-management/LytTeamManagement'));
const cadAvlSettings = lazy(() => import('src/routes/settings/cad-avl'));
const tripAnalyzer = lazy(() => import('src/routes/trip-analyzer'));
const notFound = lazy(() => import('src/routes/404'));

export const NotFoundPage = notFound;

/**
 * Single source of truth for authenticated app routes and sidebar navigation.
 *
 * Each entry may define:
 * - `routePath` / `component` — React Router registration
 * - `navPath` / `nav` — sidebar link metadata
 * - `allowedRoles` — who may access the route (normalised backend role_name values)
 * - `agencyTypes` — which agency types may access the route/nav (null = all types)
 * - `featureFlag` — key from FEATURE_FLAG_KEYS; entry only when flag is ON
 * - `featureFlagOff` — key from FEATURE_FLAG_KEYS; entry only when flag is OFF
 *   (omit both = always enabled for role/agency checks)
 */
export const NAV_REGISTRY = Object.freeze([
    {
        id: 'live-operations',
        routePath: bookList.live_operations.general,
        navPath: bookList.live_operations.default,
        component: liveOperations,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: null,
        nav: {
            section: NAV_SECTIONS.OVERVIEW,
            title: 'Live Operations',
            key: 'live-operations',
            iconName: ICON_NAMES.SidebarLiveMap,
            audience: NAV_AUDIENCE.ALL,
        },
    },
    {
        id: 'alerts',
        routePath: bookList.alerts,
        component: alerts,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: [AGENCY_TYPES.TRANSIT, AGENCY_TYPES.SNOW],
    },
    {
        id: 'transit-insights',
        routePath: bookList.transit_insights,
        navPath: bookList.transit_insights,
        component: transitInsights,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: [AGENCY_TYPES.TRANSIT, AGENCY_TYPES.SNOW],
        nav: {
            section: NAV_SECTIONS.OVERVIEW,
            title: 'Route Performance',
            key: 'transit-insights',
            iconName: ICON_NAMES.SidebarRoutes,
            audience: NAV_AUDIENCE.ALL,
        },
    },
    {
        id: 'system-health',
        routePath: bookList.system_health.general,
        navPath: bookList.system_health.entry_point,
        component: systemHealth,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: [AGENCY_TYPES.TRANSIT, AGENCY_TYPES.SNOW],
        nav: {
            section: NAV_SECTIONS.OVERVIEW,
            title: 'System Health',
            key: 'system-health',
            iconName: ICON_NAMES.SidebarSystemHealth,
            audience: NAV_AUDIENCE.ALL,
        },
    },
    {
        id: 'trip-analyzer',
        routePath: bookList.trip_analyzer,
        navPath: bookList.trip_analyzer,
        component: tripAnalyzer,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: [AGENCY_TYPES.TRANSIT, AGENCY_TYPES.SNOW],
        featureFlag: FEATURE_FLAG_KEYS.TRIP_ANALYZER,
        nav: {
            section: NAV_SECTIONS.OVERVIEW,
            title: 'Trip Analyzer',
            key: 'trip-analyzer',
            iconName: ICON_NAMES.SidebarRoutes,
            audience: NAV_AUDIENCE.ALL,
        },
    },
    {
        id: 'team',
        routePath: bookList.team,
        navPath: bookList.team,
        component: team,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: null,
        nav: {
            section: NAV_SECTIONS.TEAM_MANAGEMENT,
            title: 'Team',
            key: 'team',
            iconName: ICON_NAMES.SidebarTeam,
            audience: NAV_AUDIENCE.ALL,
        },
    },
    {
        id: 'requests',
        routePath: bookList.requests,
        navPath: bookList.requests,
        component: requests,
        allowedRoles: ADMIN_ROLES,
        agencyTypes: null,
        nav: {
            section: NAV_SECTIONS.TEAM_MANAGEMENT,
            title: 'Requests',
            key: 'requests',
            iconName: ICON_NAMES.SidebarRequests,
            audience: NAV_AUDIENCE.ADMIN,
        },
    },
    {
        id: 'lyt-team-management',
        routePath: bookList.lyt_team_management,
        navPath: bookList.lyt_team_management,
        component: lytTeamManagement,
        allowedRoles: ROLE_GROUPS.LYT_INTERNAL,
        agencyTypes: null,
        nav: {
            section: NAV_SECTIONS.INTERNAL_TOOLS,
            title: 'LYT Team Management',
            key: 'lyt-team-management',
            iconName: ICON_NAMES.SidebarLytTeam,
            noIconColor: true,
            audience: NAV_AUDIENCE.LYT_INTERNAL,
        },
    },
    {
        id: 'cad-avl',
        routePath: bookList.settings.cad_avl,
        navPath: bookList.settings.cad_avl,
        component: cadAvlSettings,
        allowedRoles: ALL_AUTHENTICATED_ROLES,
        agencyTypes: [AGENCY_TYPES.EMERGENCY],
        featureFlag: FEATURE_FLAG_KEYS.CAD_AVL_SETTINGS,
        nav: {
            section: NAV_SECTIONS.SETTINGS,
            title: 'CAD / AVL',
            key: 'cad-avl',
            iconName: ICON_NAMES.SidebarCadAvl,
            audience: NAV_AUDIENCE.ALL,
        },
    },
]);

/** Fallback route always registered regardless of role/agency context. */
export const NOT_FOUND_ROUTE = Object.freeze({
    id: 'not-found',
    routePath: bookList.notFound,
    component: notFound,
    isDefault: true,
});

/**
 * Resolves the experience home route.
 *
 * @returns {string} The default route for the active experience.
 */
export const getDefaultRoute = () => bookList.live_operations.default;
